import { Link } from "react-router-dom";
import { Network, ArrowRight, ShieldCheck, FileSpreadsheet, FileText, Search, Sparkles, Lock, Zap, Check, Download } from "lucide-react";

const HERO_FLOWS = [
  "M 330 340 C 440 300 545 330 612 438",
  "M 330 388 C 452 376 548 396 618 476",
  "M 328 432 C 452 458 548 460 610 508",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <header className="border-b border-[#1E2A3F] sticky top-0 z-40 bg-[#0B0F17]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-24 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo-link" className="flex items-center transition-opacity hover:opacity-80">
            <img src="/linkviewer-logo.png" alt="LinkViewer.PRO — home" className="h-11 sm:h-[89px] w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/faq" data-testid="nav-faq-link" className="hidden md:inline text-sm text-slate-300 hover:text-white px-3 py-2 whitespace-nowrap">FAQ</Link>
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-slate-300 hover:text-white px-3 py-2 whitespace-nowrap">Sign in</Link>
            <Link to="/register" data-testid="nav-register-link" className="hidden sm:flex text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg font-medium transition-colors items-center gap-1.5 whitespace-nowrap">Start free <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative niq-grid-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0B0F17]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/25 bg-blue-500/10 text-blue-300 text-xs font-mono-niq uppercase tracking-wider mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Profile intelligence engine
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
              Your professional network,<br/>finally <span className="text-[#2FAFDD]">intelligent.</span>
            </h1>
            <p className="text-slate-400 text-base sm:text-lg leading-relaxed max-w-lg mb-8">
              Upload the contact network you've exported from your LinkedIn profile (CSV, ZIP, XLS). LinkViewer.PRO analyses and understands every contact, selects the ones that best match your specific queries and chosen search parameters, and generates a professional PDF or XLS report. It only works with the file you provide. No scraping, online lookups, or outreach involved.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-button" className="bg-blue-600 hover:bg-blue-500 px-6 py-3.5 rounded-lg font-medium transition-colors flex items-center gap-2">
                Analyze my network free <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-orange-300 border border-orange-500/50 bg-orange-500/[0.08] px-5 py-3 rounded-full font-mono-niq shadow-lg shadow-orange-950/20">
                <Sparkles className="w-4 h-4 text-orange-400" /> 3 free searches · no card
              </span>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-xs sm:text-sm text-slate-400" data-testid="hero-formats-badge">
              <FileSpreadsheet className="w-4 h-4 text-blue-400 shrink-0" />
              Works with your exported connections file <span className="text-slate-200 font-medium">CSV</span>, <span className="text-slate-200 font-medium">Excel</span> or <span className="text-slate-200 font-medium">.Zip</span>
            </div>
          </div>

          {/* Hero visual - how it works */}
          <div className="animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-2xl border border-[#27334D] bg-[#0D121C] p-2 shadow-2xl shadow-blue-950/30 overflow-hidden">
              <div className="absolute -inset-24 bg-blue-600/10 blur-3xl rounded-full pointer-events-none" />
              <div className="relative">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/e2e5360a-870b-430f-8173-bf685e2eeb7d/images/82712546ebf0fe14dbda2577f64fa3f5bbd2cc08b6bc43df9560fd249f20d76c.jpeg"
                  alt="LinkViewer.PRO organizes a contacts CSV into a clean, categorized, searchable list"
                  data-testid="hero-visual"
                  className="relative w-full rounded-xl object-cover"
                  loading="eager"
                />
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1024 1024" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                  <defs>
                    {HERO_FLOWS.map((d, i) => <path key={i} id={`niqFlow${i}`} d={d} fill="none" />)}
                    <filter id="niqGlow" x="-60%" y="-60%" width="220%" height="220%">
                      <feGaussianBlur stdDeviation="5" result="b" />
                      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>
                  {HERO_FLOWS.flatMap((d, i) =>
                    [0, 0.55, 1.1, 1.65, 2.2].map((off, j) => {
                      const begin = (i * 0.3 + off).toFixed(2);
                      return (
                        <circle key={`${i}-${j}`} r={j % 2 ? 4 : 6} fill={j % 2 ? "#7dd3fc" : "#38bdf8"} filter="url(#niqGlow)" opacity="0">
                          <animateMotion dur="2.9s" begin={`${begin}s`} repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines="0.4 0 0.2 1">
                            <mpath xlinkHref={`#niqFlow${i}`} />
                          </animateMotion>
                          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.82;1" dur="2.9s" begin={`${begin}s`} repeatCount="indefinite" />
                        </circle>
                      );
                    })
                  )}
                </svg>
              </div>
              <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-2.5 p-3.5">
                {[
                  { n: "01", t: "Upload CSV · XLS · ZIP", icon: FileSpreadsheet },
                  { n: "02", t: "AI ranks & explains", icon: Sparkles },
                  { n: "03", t: "Export report", icon: FileText },
                ].map((s) => (
                  <div key={s.n} className="rounded-lg border border-[#1E2A3F] bg-[#111726] px-4 py-3 sm:py-4 flex flex-row sm:flex-col items-center sm:text-center gap-3 sm:gap-0">
                    <div className="flex items-center gap-2 text-blue-400 sm:mb-1.5 shrink-0"><s.icon className="w-4.5 h-4.5" /><span className="font-mono-niq text-xs text-slate-500">{s.n}</span></div>
                    <div className="text-sm font-medium text-slate-200 whitespace-nowrap sm:whitespace-normal">{s.t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRIVACY PROMISE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0"><ShieldCheck className="w-6 h-6 text-emerald-400" /></div>
          <div className="flex-1">
            <h3 className="font-display text-xl font-semibold mb-1">No scraping. No online lookups. Ever.</h3>
            <p className="text-slate-400 text-sm leading-relaxed">LinkViewer.PRO performs no web browsing, no data scraping, and no external lookups of any kind. It works exclusively on a CSV, Excel or ZIP file you already own and obtained from a legitimate source you have the right to use. All it does is read that file and apply keyword-based categorization, labelling each row by sector, seniority and region so you can filter and search it. It never fetches, adds, or collects new information about anyone, and it never contacts or messages anyone. It is not a scraper, a lead-generation tool, or an outreach platform.</p>
          </div>
        </div>
      </section>

      {/* HOW TO GET YOUR FILE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-2xl border border-blue-500/25 bg-blue-500/[0.05] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0"><Download className="w-6 h-6 text-blue-400" /></div>
          <div className="flex-1">
            <h3 className="font-display text-xl font-semibold mb-1">How to download your contacts from LinkedIn</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Don't have your file yet? It takes about 2 minutes — LinkedIn emails you a ZIP, and you upload it here as it is. Follow the simple step-by-step guide.</p>
          </div>
          <Link to="/how-to-download" data-testid="home-download-guide-btn" className="w-full sm:w-auto text-center bg-blue-600 hover:bg-blue-500 px-6 py-3.5 rounded-lg font-medium transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0">
            See the guide <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Zap, title: "Automatic categorization", desc: "The moment you upload, every single connection is auto-labelled by sector, seniority and region. It runs fast, caches its work so repeat uploads are instant, and stays cost-efficient even across a network of tens of thousands of contacts." },
            { icon: Search, title: "Ask it your way", desc: "Narrow by one or more regions and sectors, or simply describe what you need in a talkative prompt. Either way you get an intelligent, ranked shortlist from your own file, each with a one-line reason." },
            { icon: Lock, title: "Clear, honest labels", desc: "Every category comes with a simple High, Medium or Low accuracy mark, so you always know which labels to trust and which are a softer inference. No fake precision, no black boxes, just an honest read of your own data." },
          ].map((f, i) => (
            <div key={i} className="rounded-2xl border border-[#27334D] bg-[#111726] p-6 hover:border-blue-500/30 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-blue-600/15 border border-blue-500/20 flex items-center justify-center mb-4"><f.icon className="w-5 h-5 text-blue-400" /></div>
              <h3 className="font-display text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EXPORT PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-6 flex items-center gap-4">
            <FileText className="w-9 h-9 text-red-400 shrink-0" />
            <div><h4 className="font-medium">PDF report</h4><p className="text-slate-500 text-sm">Clean, shareable and ready to keep.</p></div>
          </div>
          <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-6 flex items-center gap-4">
            <FileSpreadsheet className="w-9 h-9 text-emerald-400 shrink-0" />
            <div><h4 className="font-medium">Excel export</h4><p className="text-slate-500 text-sm">All your categorized columns, ready to filter.</p></div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Simple pricing</h2>
          <p className="text-slate-400">Start free. Upgrade when your network gets serious.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 items-stretch">
          <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-7 flex flex-col h-full">
            <div className="label-mono text-slate-500 mb-2">Free</div>
            <div className="font-display text-4xl font-bold mb-1">$0</div>
            <div className="text-xs text-slate-500 mb-5">No card required</div>
            <ul className="space-y-2.5 text-sm text-slate-400 mb-6 flex-1">
              {["1 dataset upload", "Auto-built dashboard & filters", "3 searches (view-only)", "No PDF / Excel downloads"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-slate-500" /> {t}</li>
              ))}
            </ul>
            <Link to="/register" data-testid="pricing-free-btn" className="block text-center px-4 py-3 rounded-lg bg-white text-black hover:bg-slate-200 text-sm font-semibold transition-colors">Get Started For Free</Link>
          </div>

          <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-7 flex flex-col h-full">
            <div className="label-mono text-blue-300 mb-2">Pro · Monthly</div>
            <div className="font-display text-4xl font-bold mb-1">$12<span className="text-lg text-slate-500 font-normal">/month</span></div>
            <div className="text-xs text-slate-500 mb-5">Billed monthly</div>
            <ul className="space-y-2.5 text-sm text-slate-300 mb-6 flex-1">
              {["Unlimited searches", "Unlimited PDF + Excel downloads", "Unlimited datasets", "Priority AI categorization speed", "Cancel anytime"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-blue-400" /> {t}</li>
              ))}
            </ul>
            <Link to="/billing?interval=month" data-testid="pricing-monthly-btn" className="block text-center px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-500 text-sm font-semibold transition-colors">Subscribe Monthly</Link>
          </div>

          <div className="rounded-2xl border border-orange-500/40 bg-orange-500/[0.06] p-7 relative flex flex-col h-full">
            <div className="absolute -top-3 left-7 text-[10px] font-mono-niq uppercase tracking-wider bg-orange-500 px-2.5 py-1 rounded-full">Best value</div>
            <div className="label-mono text-orange-400 mb-2">Pro · Annual</div>
            <div className="font-display text-4xl font-bold mb-1">$99<span className="text-lg text-slate-500 font-normal">/year</span></div>
            <div className="text-xs text-emerald-400 mb-5">Save ~31% vs monthly</div>
            <ul className="space-y-2.5 text-sm text-slate-300 mb-6 flex-1">
              {["Unlimited searches", "Unlimited PDF + Excel downloads", "Unlimited datasets", "Priority AI categorization speed", "Cancel anytime"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-400" /> {t}</li>
              ))}
            </ul>
            <Link to="/billing?interval=year" data-testid="pricing-annual-btn" className="block text-center px-4 py-3 rounded-lg bg-orange-500 text-white hover:bg-orange-400 text-sm font-semibold transition-colors">Subscribe Annually</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#1E2A3F] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <Link to="/" className="flex items-center"><img src="/linkviewer-logo.png" alt="LinkViewer.PRO" className="h-6 w-auto opacity-90" /></Link>
            <div className="flex items-center gap-5 text-xs">
              <Link to="/how-to-download" className="hover:text-slate-300 transition-colors">Get your file from LinkedIn</Link>
              <Link to="/faq" className="hover:text-slate-300 transition-colors">FAQ</Link>
              <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms &amp; Conditions</Link>
              <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
              <Link to="/refund" className="hover:text-slate-300 transition-colors">Refund Policy</Link>
              <span className="font-mono-niq text-slate-600">Never scraped</span>
            </div>
          </div>
          <div className="border-t border-[#1E2A3F] pt-4 text-center text-xs text-slate-600 font-mono-niq space-y-1.5">
            <div>LinkedIn is a trademark of LinkedIn Corporation. LinkViewer.PRO is not affiliated with, endorsed by or sponsored by LinkedIn.</div>
            <div>2026 © LinkViewer.PRO. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
